import { JSX, useRef, useState, FocusEvent, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';

export interface NavItemProps {
  label: string | ReactNode;
  icon?: JSX.Element;
  to: string;
  isActive?: boolean;
  isParent?: boolean;
  isCollapsed?: boolean;
  subMenu?: NavItemProps[];
  onClick?: () => void;
}

const NavItem = ({
  label,
  icon,
  to,
  isActive,
  isParent = false,
  isCollapsed,
  subMenu,
  onClick,
}: NavItemProps) => {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const containerRef = useRef<HTMLLIElement>(null);

  const showTooltip = isCollapsed && (isHovered || isDetailsOpen);
  const itemClassName = `w-full text-left gap-3 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-base-content ${isCollapsed ? 'justify-center' : ''} ${isActive ? 'menu-active text-base-content bg-black/5!' : ''}`;

  const handleBlur = (e: FocusEvent) => {
    if (
      !containerRef.current?.contains(e.relatedTarget as Node) &&
      isCollapsed
    ) {
      setIsDetailsOpen(false);
    }
  };

  return (
    <li
      ref={containerRef}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onBlur={handleBlur}
    >
      {subMenu ? (
        <>
          <details open={isDetailsOpen}>
            <summary
              className={itemClassName}
              onClick={(e) => {
                e.preventDefault();
                setIsDetailsOpen(!isDetailsOpen);
              }}
            >
              <div className="flex items-center gap-3 min-w-0">
                {icon && (
                  <span className="flex shrink-0 items-center justify-center w-7 h-7 bg-base-200 text-base-content rounded-full">
                    {icon}
                  </span>
                )}
                {!isCollapsed && (
                  <span className="font-medium text-sm">{label}</span>
                )}
              </div>
            </summary>
            {!isCollapsed && isDetailsOpen && (
              <ul
                className={`gap-1 max-h-[60vh] overflow-y-auto md:max-h-none md:overflow-visible
      ${isParent ? 'scroll-smooth scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400' : ''}
    `}
              >
                {subMenu.map((item) => (
                  <NavItem
                    key={item.to}
                    label={item.label}
                    icon={item.icon}
                    to={item.to}
                    isActive={location.pathname.includes(item.to)}
                    isCollapsed={isCollapsed}
                    subMenu={item.subMenu}
                    onClick={item.onClick ?? onClick}
                  />
                ))}
              </ul>
            )}
          </details>

          {showTooltip && (
            <div className="absolute left-full top-1/2 -translate-y-1/2 z-50 ml-3 bg-primary text-primary-content text-sm px-3 py-1 rounded-box shadow">
              <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rotate-45 z-50" />
              <span>{label}</span>
              <hr className="border-l-1 h-3/4 ml-8" />
              <ul
                className={`max-h-[60vh] overflow-y-auto pr-2 md:max-h-none md:overflow-visible
      ${isParent ? 'scroll-smooth scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400' : ''}
    `}
              >
                {subMenu.map((item) => (
                  <div key={`tooltip-submenu-${item.to}`}>
                    <NavItem
                      key={item.to}
                      label={item.label}
                      to={item.to}
                      subMenu={item.subMenu}
                      onClick={item.onClick ?? onClick}
                    />
                  </div>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <>
          <NavLink to={to} onClick={onClick} className={itemClassName}>
            {icon && (
              <span className="flex shrink-0 items-center justify-center w-7 h-7 bg-base-200 text-base-content rounded-full">
                {icon}
              </span>
            )}
            {!isCollapsed && (
              <span className="font-medium text-sm">{label}</span>
            )}
          </NavLink>

          {showTooltip && (
            <div className="absolute left-full top-1/2 -translate-y-1/2 z-50 ml-3 bg-primary text-primary-content text-sm px-3 py-1 rounded-box shadow">
              <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-3 h-3 bg-primary rotate-45 z-50" />
              {label}
            </div>
          )}
        </>
      )}
    </li>
  );
};

export default NavItem;
